import {
  calculateShippingFee,
  findShippingRegion,
  type QuoteRequestForm,
  type ShippingFeeSettings,
  type ShippingRegion,
  splitQuoteAmounts,
  splitVatInclusiveTotal,
} from '@repo/api-contracts';

export type QuotePriceTier = QuoteRequestForm['priceTiers'][number];

/** 임시 견적 — 적용 구간과 부가세 포함 합계·공급가액·부가세(견적 설계 §5-5). */
export interface QuoteEstimate {
  readonly tier: QuotePriceTier;
  readonly totalAmount: number;
  readonly supplyAmount: number;
  readonly vatAmount: number;
}

/** 적용 구간 — minQty ≤ 수량인 구간 중 가장 큰 minQty. 구간 순서에 의존하지 않는다. */
export const findActiveTier = (
  tiers: readonly QuotePriceTier[],
  quantity: number,
): QuotePriceTier | null =>
  tiers.reduce<QuotePriceTier | null>(
    (active, tier) =>
      tier.minQty <= quantity && (active === null || tier.minQty > active.minQty) ? tier : active,
    null,
  );

/**
 * 수량 × 적용 구간 단가 = 부가세 포함 합계. 공급가액·부가세는 서버의 calculateQuoteAmounts 와 같은 공용 함수
 * (`splitVatInclusiveTotal`)로 나눠 제출 뒤 초안 줄 금액과 일치한다.
 */
export const estimateQuote = (
  tiers: readonly QuotePriceTier[],
  quantity: number,
): QuoteEstimate | null => {
  if (!Number.isInteger(quantity) || quantity < 1) {
    return null;
  }

  const tier = findActiveTier(tiers, quantity);

  if (tier === null) {
    return null;
  }

  const totalAmount = tier.unitPrice * quantity;

  return { tier, totalAmount, ...splitVatInclusiveTotal(totalAmount) };
};

/** 임시 견적의 배송지 한 곳 — 지역을 아직 모르면(우편번호 미정) region 은 null 이고 기본 배송비로 센다. */
export interface ShippingEstimateLine {
  readonly region: ShippingRegion | null;
  readonly fee: number;
}

export interface ShippingEstimate {
  readonly lines: readonly ShippingEstimateLine[];
  readonly shippingAmount: number;
  /** 우편번호를 아직 고르지 않은(또는 5자리 숫자가 아닌) 카드가 있다 — 지역 추가 요금이 빠졌을 수 있다. */
  readonly hasPendingAddress: boolean;
}

/**
 * 배송지마다 배송비(spec D1·D2) — 앞뒤 공백을 지운 우편번호가 5자리 숫자면 지역을 판별하고(서버 `postalCodeSchema` 와 같다),
 * 아니면 기본 배송비로 센다(spec §6-1 "주소를 아직 고르지 않은 카드는 기본 배송비"). 형식 이상을 일반 지역으로 보지 않는다(R1).
 */
export const estimateShipping = (
  postalCodes: readonly string[],
  settings: ShippingFeeSettings,
): ShippingEstimate => {
  const lines = postalCodes.map((raw): ShippingEstimateLine => {
    const region = findShippingRegion(raw.trim());
    return {
      region,
      fee: region === null ? settings.baseFee : calculateShippingFee(region, settings),
    };
  });
  return {
    lines,
    shippingAmount: lines.reduce((sum, line) => sum + line.fee, 0),
    hasPendingAddress: lines.some((line) => line.region === null),
  };
};

/** 임시 견적(배송비 포함) — 상품 금액·배송비·부가세 포함 합계·공급가액·부가세. */
export interface QuoteEstimateWithShipping {
  readonly tier: QuotePriceTier;
  readonly productAmount: number;
  readonly shipping: ShippingEstimate;
  readonly totalAmount: number;
  readonly supplyAmount: number;
  readonly vatAmount: number;
}

/**
 * 상품 금액(적용 단가 × 총 수량) + 배송지별 배송비. 합계·공급가·부가세는 서버 초안 줄 합과 같은 공용 함수(`splitQuoteAmounts`)로
 * 나눈다 — 제출 뒤 초안 금액과 일치한다. 단가 구간이 없거나 수량이 올바르지 않으면 null.
 */
export const estimateQuoteWithShipping = (
  tiers: readonly QuotePriceTier[],
  quantity: number,
  postalCodes: readonly string[],
  settings: ShippingFeeSettings,
): QuoteEstimateWithShipping | null => {
  const product = estimateQuote(tiers, quantity);
  if (product === null) return null;
  const shipping = estimateShipping(postalCodes, settings);
  const amounts = splitQuoteAmounts([
    { kind: 'PRODUCT', amount: product.totalAmount },
    ...shipping.lines.map((line) => ({ kind: 'SHIPPING' as const, amount: line.fee })),
  ]);
  return {
    tier: product.tier,
    productAmount: amounts.productAmount,
    shipping,
    totalAmount: amounts.totalAmount,
    supplyAmount: amounts.supplyAmount,
    vatAmount: amounts.vatAmount,
  };
};
